/**
 * Logo — classrooms.work brand mark
 * The book mascot (a friendly book with blinking eyes) on a sky blue chip with
 * an AI sparkle, next to the wordmark. Matches the landing-page mascots so the
 * brand carries one character everywhere. Scales down to a favicon.
 */
import React from 'react';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  showWordmark?: boolean;
  /** 'light' = white text for dark/colored backgrounds; 'dark' = default dark text */
  variant?: 'light' | 'dark';
  onClick?: () => void;
  className?: string;
}

const SIZES = {
  sm: { tile: 'h-8 w-8', word: 'text-lg' },
  md: { tile: 'h-10 w-10', word: 'text-xl' },
  lg: { tile: 'h-14 w-14', word: 'text-3xl' },
};

const INK = '#1f2937';

/** The mark itself, exported so the favicon/app icons can reuse the artwork. */
export const LogoMark: React.FC<{ className?: string; animated?: boolean }> = ({ className, animated = true }) => (
  <svg viewBox="0 0 48 48" className={className} fill="none" aria-hidden="true">
    {/* sky blue chip */}
    <circle cx="24" cy="24" r="24" fill="#93c5fd" />
    {/* book with a face */}
    <rect x="13" y="12" width="22" height="27" rx="3.5" fill="#ffffff" stroke={INK} strokeWidth={2.4} />
    <path d="M17.5 12v27" stroke={INK} strokeWidth={2.2} />
    <ellipse className={animated ? 'mascot-eye' : undefined} cx="23.5" cy="23" rx="1.7" ry="2.1" fill={INK} />
    <ellipse className={animated ? 'mascot-eye' : undefined} cx="30.5" cy="23" rx="1.7" ry="2.1" fill={INK} />
    <path d="M24.3 29c1.5 1.7 4.1 1.7 5.6 0" stroke={INK} strokeWidth={2} strokeLinecap="round" />
    {/* AI sparkle */}
    <path
      d="M37 5.5l1.7 3.8 3.8 1.7-3.8 1.7L37 16.5l-1.7-3.8-3.8-1.7 3.8-1.7z"
      fill="#ffffff"
      stroke={INK}
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
  </svg>
);

const Logo: React.FC<LogoProps> = ({ size = 'md', showWordmark = true, variant = 'dark', onClick, className = '' }) => {
  const s = SIZES[size];
  const wordColor = variant === 'light' ? 'text-white' : 'text-gray-900 dark:text-white';

  return (
    <div
      onClick={onClick}
      className={`inline-flex select-none items-center gap-2.5 ${onClick ? 'cursor-pointer' : ''} ${className}`}
    >
      <LogoMark className={`flex-shrink-0 ${s.tile}`} />

      {showWordmark && (
        <span className={`font-extrabold tracking-tight ${s.word} ${wordColor}`}>
          classrooms<span className="text-blue-400">.</span>
        </span>
      )}
    </div>
  );
};

export default Logo;
